/**
 * Canvas view transform, grid and snapping.
 *
 * The world is plan millimetres with Y increasing upwards, the way a roof plan
 * is dimensioned. The canvas has Y increasing downwards. Keeping that flip in
 * one place means no drawing code has to remember it.
 */

export interface ViewTransform {
  /** Screen pixels per world millimetre. */
  scale: number;
  /** World coordinate at the canvas origin, in millimetres. */
  offsetX: number;
  offsetY: number;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface WorldPoint {
  x: number;
  y: number;
}

export const DEFAULT_VIEW: ViewTransform = { scale: 0.02, offsetX: -2000, offsetY: -2000 };

export const MIN_SCALE = 0.0008;
export const MAX_SCALE = 2;

export function toScreen(view: ViewTransform, world: WorldPoint, height: number): ScreenPoint {
  return {
    x: (world.x - view.offsetX) * view.scale,
    y: height - (world.y - view.offsetY) * view.scale,
  };
}

export function toWorld(view: ViewTransform, screen: ScreenPoint, height: number): WorldPoint {
  return {
    x: screen.x / view.scale + view.offsetX,
    y: (height - screen.y) / view.scale + view.offsetY,
  };
}

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** Zooms about a fixed screen point, so the point under the cursor stays put. */
export function zoomAt(
  view: ViewTransform, screen: ScreenPoint, factor: number, height: number,
): ViewTransform {
  const before = toWorld(view, screen, height);
  const scale = clampScale(view.scale * factor);
  const after = toWorld({ ...view, scale }, screen, height);
  return { scale, offsetX: view.offsetX + before.x - after.x, offsetY: view.offsetY + before.y - after.y };
}

/**
 * Applies one two-finger update: zoom about the gesture centre, then follow
 * the fingers' travel.
 *
 * The zoom is resolved first so the pan is expressed in the scale the drawing
 * ends the frame at; panning at the old scale would drift away from the
 * fingers as the zoom changes.
 */
export function pinchView(
  view: ViewTransform,
  centre: ScreenPoint,
  factor: number,
  panX: number,
  panY: number,
  height: number,
): ViewTransform {
  const zoomed = zoomAt(view, centre, factor, height);
  return {
    ...zoomed,
    offsetX: zoomed.offsetX - panX / zoomed.scale,
    offsetY: zoomed.offsetY + panY / zoomed.scale,
  };
}

/** Fits a world bounding box into the canvas with a margin. */
export function fitBounds(
  min: WorldPoint, max: WorldPoint, width: number, height: number, marginPx = 48,
): ViewTransform {
  const worldWidth = Math.max(max.x - min.x, 1000);
  const worldHeight = Math.max(max.y - min.y, 1000);
  const scale = clampScale(Math.min(
    (width - 2 * marginPx) / worldWidth,
    (height - 2 * marginPx) / worldHeight,
  ));
  // Centre the content: half the leftover screen space on each side.
  const offsetX = min.x - (width / scale - worldWidth) / 2;
  const offsetY = min.y - (height / scale - worldHeight) / 2;
  return { scale, offsetX, offsetY };
}

/**
 * Chooses a grid spacing that stays legible at the current zoom.
 *
 * A fixed spacing either disappears when zoomed out or fills the canvas with
 * lines when zoomed in. The step climbs through 1, 2 and 5 times a power of
 * ten, which is what technical drawings use.
 */
export function gridStep(scale: number, targetPixels = 40): number {
  const rawStep = targetPixels / scale;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export interface SnapCandidate {
  x: number;
  y: number;
  /** What the position snapped to, for the cursor readout. */
  kind: 'free' | 'grid' | 'point';
  pointId?: string;
}

export interface SnapSettings {
  gridSizeMM: number;
  toGrid: boolean;
  toPoints: boolean;
  orthogonal: boolean;
}

/**
 * Resolves a cursor position to the point that should be used.
 *
 * Existing points win over the grid: a user aiming at a vertex means that
 * vertex, and landing a fraction of a millimetre away from it creates the
 * duplicate-point and short-segment errors the validator then reports.
 */
export function snap(
  world: WorldPoint,
  view: ViewTransform,
  settings: SnapSettings,
  points: readonly { id: string; x: number; y: number }[],
  anchor?: WorldPoint,
): SnapCandidate {
  if (settings.toPoints) {
    // A fixed screen radius, so the target stays the same size at any zoom.
    const radiusWorld = 10 / view.scale;
    let best: { id: string; x: number; y: number } | undefined;
    let bestDistance = radiusWorld;
    for (const p of points) {
      const distance = Math.hypot(p.x - world.x, p.y - world.y);
      if (distance <= bestDistance) {
        best = p;
        bestDistance = distance;
      }
    }
    if (best) return { x: best.x, y: best.y, kind: 'point', pointId: best.id };
  }

  let { x, y } = world;

  if (settings.orthogonal && anchor) {
    // Constrain to whichever axis the cursor has travelled further along.
    if (Math.abs(x - anchor.x) >= Math.abs(y - anchor.y)) {
      y = anchor.y;
    } else {
      x = anchor.x;
    }
  }

  if (settings.toGrid) {
    const step = Number.isFinite(settings.gridSizeMM) && settings.gridSizeMM > 0
      ? settings.gridSizeMM
      : gridStep(view.scale);
    return { x: Math.round(x / step) * step, y: Math.round(y / step) * step, kind: 'grid' };
  }

  return { x, y, kind: 'free' };
}

/** Formats a world length for the cursor readout and segment labels. */
export function formatMillimetres(value: number): string {
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toLocaleString('en-GB', { maximumFractionDigits: 3 })} m`;
  }
  return `${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })} mm`;
}
